import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Send, Link2, Copy, Check, Loader2, MailCheck, Clock, Eye, PenLine, BellRing, CheckCircle2 } from "lucide-react";

export default function OutlookShare({ leadId, defaultEmail }) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(defaultEmail || "");
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [giorni, setGiorni] = useState(10);

  useEffect(() => { setEmail(defaultEmail || ""); }, [defaultEmail]);

  const publicUrl = info?.token ? `${window.location.origin}/p/${info.token}` : "";

  const loadLink = async () => {
    try {
      const { data } = await api.get(`/leads/${leadId}/outlook-link`);
      setInfo(data);
      return data;
    } catch (e) { return null; }
  };
  useEffect(() => { loadLink(); /* eslint-disable-next-line */ }, [leadId]);

  const copyLink = async () => {
    let d = info;
    if (!d?.token) d = await loadLink();
    const url = d?.token ? `${window.location.origin}/p/${d.token}` : "";
    if (!url) { toast.error("Link non disponibile"); return; }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.message(url);
    }
  };

  const send = async () => {
    if (!email.trim() || !email.includes("@")) { toast.error("Inserisci un'email valida"); return; }
    const g = Math.max(1, Math.min(Number(giorni) || 10, 20));
    setSending(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/send-outlook`, { email: email.trim(), origin_url: window.location.origin, giorni_validita: g });
      toast.success(`Proposta inviata a ${data.sent_to}`);
      await loadLink();
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 no-print" data-testid="outlook-share">
      <Button size="sm" onClick={() => setOpen(true)} className="font-semibold" data-testid="send-outlook-btn">
        <Send className="h-4 w-4 mr-2" /> Invia proposta via email
      </Button>
      <Button size="sm" variant="outline" onClick={copyLink} data-testid="copy-link-btn">
        {copied ? <Check className="h-4 w-4 mr-2 text-green-500" /> : <Link2 className="h-4 w-4 mr-2" />} Copia link
      </Button>
      {info?.sent_at && (
        <span className="inline-flex items-center gap-1.5 text-xs text-green-500 font-medium" data-testid="sent-status">
          <MailCheck className="h-3.5 w-3.5" /> Inviata a {info.sent_to} · {new Date(info.sent_at).toLocaleDateString("it-IT")}{info.sent_count > 1 ? ` (${info.sent_count}×)` : ""}
        </span>
      )}
      {info?.expires_at && (
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="link-expiry">
          <Clock className="h-3.5 w-3.5" /> Link valido fino al {new Date(info.expires_at).toLocaleDateString("it-IT")}
        </span>
      )}

      {info?.token && (
        <div className="w-full flex flex-wrap items-center gap-1.5 mt-1" data-testid="outlook-status-badges">
          {info.caparra_pagata ? (
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-green-500/15 text-green-600" data-testid="badge-paid">
              <CheckCircle2 className="h-3 w-3" /> Ordine confermato
            </span>
          ) : info.signed ? (
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-emerald-500/15 text-emerald-600" data-testid="badge-signed">
              <PenLine className="h-3 w-3" /> Firmata{info.signed_at ? ` · ${new Date(info.signed_at).toLocaleDateString("it-IT")}` : ""}
            </span>
          ) : info.opened_at ? (
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-blue-500/15 text-blue-600" data-testid="badge-opened">
              <Eye className="h-3 w-3" /> Aperta · {new Date(info.opened_at).toLocaleDateString("it-IT")}{info.opened_count > 1 ? ` (${info.opened_count}×)` : ""}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-secondary text-muted-foreground" data-testid="badge-not-opened">
              <Eye className="h-3 w-3" /> Non ancora aperta
            </span>
          )}
          {info.reminder_sent_at && (
            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-amber-500/15 text-amber-600" data-testid="badge-reminder">
              <BellRing className="h-3 w-3" /> Promemoria inviato · {new Date(info.reminder_sent_at).toLocaleDateString("it-IT")}
            </span>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-popover" data-testid="send-outlook-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Invia la proposta al cliente</DialogTitle>
            <DialogDescription>Il cliente riceverà un link riservato da cui potrà visualizzare la proposta, firmare e pagare la caparra in autonomia.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Email destinatario</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@email.it" data-testid="send-email-input" className="mt-1" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Scadenza link (giorni)</label>
              <Input type="number" min={1} max={20} value={giorni} onChange={(e) => setGiorni(e.target.value)} data-testid="send-giorni-input" className="mt-1 max-w-[140px]" />
              <p className="text-[11px] text-muted-foreground mt-1">Consigliati 10 giorni · massimo 20.</p>
            </div>
            {publicUrl && (
              <div className="rounded-lg border border-border bg-secondary/30 p-2.5 flex items-center gap-2">
                <span className="text-xs text-muted-foreground truncate flex-1" data-testid="public-link-preview">{publicUrl}</span>
                <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={copyLink}><Copy className="h-3.5 w-3.5" /></Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={send} disabled={sending} className="font-semibold" data-testid="confirm-send-btn">
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Invia proposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
